<template>
  <div class="model-section">
    <div v-if="loading" class="model-loading">
      <div class="model-spinner"></div>
      Đang tải danh sách mô hình...
    </div>
    <div v-else-if="!models.length" class="model-empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
      Chưa có mô hình AI. Vui lòng kiểm tra cấu hình hệ thống.
    </div>
    <div v-else class="model-row">
      <!-- Left: model select -->
      <div class="model-col">
        <label class="model-label">
          Mô hình AI <span class="req">*</span>
          <span class="model-info" :title="selectedHint || 'Mô hình AI sẽ phân tích dữ liệu của bạn'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
          </span>
        </label>
        <el-select
          :value="value"
          size="mini"
          placeholder="Chọn mô hình AI"
          style="width:100%"
          @change="$emit('input', $event)">
          <el-option
            v-for="m in filteredModels"
            :key="m.modelKey"
            :label="m.badge"
            :value="m.modelKey">
            <el-tooltip
              :disabled="!m.hint"
              :content="m.hint"
              placement="right"
              effect="dark"
              :open-delay="200"
              popper-class="model-hint-popper">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;width:100%">
                <span>{{ m.icon }} {{ m.badge }}</span>
                <span style="color:var(--t3);font-size:11px;flex-shrink:0">{{ m.provider }}</span>
              </div>
            </el-tooltip>
          </el-option>
        </el-select>
      </div>

      <!-- Right: provider filter -->
      <div class="model-col">
        <label class="model-label">
          Nhà cung cấp
          <span class="model-info" title="Chọn nhà cung cấp để chỉ hiển thị mô hình của nhà cung cấp đó">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
          </span>
        </label>
        <el-select
          :value="providerFilter"
          size="mini"
          placeholder="Tất cả nhà cung cấp"
          style="width:100%"
          @change="onProviderChange">
          <el-option label="Tất cả nhà cung cấp" value="" />
          <el-option
            v-for="p in providers"
            :key="p"
            :label="providerIcon(p) + ' ' + p"
            :value="p" />
        </el-select>
      </div>
    </div>
  </div>
</template>

<script>
// Preferred model per provider (matched loosely on provider name + modelKey)
const PREFERRED = [
  { provider: 'openai', model: 'gpt-4o' },
  { provider: 'gemini', model: 'gemini-2.5-flash-lite' },
  { provider: 'google', model: 'gemini-2.5-flash-lite' },
];

// Default model when no provider filter is applied ("Tất cả")
const ALL_DEFAULT_MODEL = 'gpt-4o';

export default {
  name: 'SaleAIModelSection',
  props: {
    value: { type: String, default: '' },
    models: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { providerFilter: '' };
  },
  computed: {
    selected() {
      return this.models.find(m => m.modelKey === this.value) || null;
    },
    selectedHint() {
      return this.selected ? this.selected.hint : '';
    },
    providers() {
      const seen = [];
      this.models.forEach(m => {
        const p = m.provider || '';
        if (p && seen.indexOf(p) < 0) seen.push(p);
      });
      return seen;
    },
    filteredModels() {
      if (!this.providerFilter) return this.models;
      return this.models.filter(m => (m.provider || '') === this.providerFilter);
    },
  },
  watch: {
    // Keep the filter consistent if a loaded config selects a model from another provider
    value() {
      const sel = this.selected;
      if (this.providerFilter && sel && (sel.provider || '') !== this.providerFilter) {
        this.providerFilter = sel.provider || '';
      }
    },
    // Pick a default model once the list loads (no filter = "Tất cả" → gpt-4o)
    models: {
      immediate: true,
      handler() {
        if (!this.value && this.models.length) {
          const key = this.providerFilter ? this.preferredModelKey(this.providerFilter) : this.allDefaultKey();
          if (key) this.$emit('input', key);
        }
      },
    },
  },
  methods: {
    providerIcon(p) {
      const m = this.models.find(x => (x.provider || '') === p);
      return m && m.icon ? m.icon : '🤖';
    },
    // Match a model by key: exact first, then loose match excluding mini/nano variants
    matchModelKey(pool, wantedLower) {
      const exact = pool.find(m => (m.modelKey || '').toLowerCase() === wantedLower);
      if (exact) return exact.modelKey;
      const loose = pool.find(m => {
        const k = (m.modelKey || '').toLowerCase();
        return k.indexOf(wantedLower) >= 0 && k.indexOf('mini') < 0 && k.indexOf('nano') < 0;
      });
      return loose ? loose.modelKey : '';
    },
    preferredModelKey(provider) {
      const pool = this.models.filter(m => (m.provider || '') === provider);
      if (!pool.length) return '';
      const lp = (provider || '').toLowerCase();
      const pref = PREFERRED.find(x => lp.indexOf(x.provider) >= 0);
      if (pref) {
        const k = this.matchModelKey(pool, pref.model.toLowerCase());
        if (k) return k;
      }
      return pool[0].modelKey;
    },
    allDefaultKey() {
      if (!this.models.length) return '';
      return this.matchModelKey(this.models, ALL_DEFAULT_MODEL) || this.models[0].modelKey;
    },
    onProviderChange(p) {
      this.providerFilter = p;
      // Provider chosen → its preferred model; "Tất cả" → gpt-4o default
      const key = p ? this.preferredModelKey(p) : this.allDefaultKey();
      if (key && key !== this.value) this.$emit('input', key);
    },
  },
};
</script>
